// src/pages/stylist/ProfilePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { usePauseIdleTimer } from '../../context/IdleLogoutContext';
import { userService } from '../../services/userService';
import  stylistService  from '../../services/stylistService';
import  portfolioService  from '../../services/portfolioService';
import { Stylist, PortfolioItem } from '../../types/stylist';
import '../../styles/StylistProfile.css';
import { 
  EditIcon, SaveIcon, CameraIcon, PhoneIcon, EmailIcon, 
  AddPhotoIcon, DeleteIcon, 
  PortfolioIcon, CloseIcon,
  SecurityIcon, NotificationsIcon, SupportIcon,
  LogoutIcon
} from '../../components/common/Icons';

// Specialties options
const SPECIALTIES_OPTIONS = [
  'Haircuts', 'Coloring', 'Styling', 'Extensions', 'Treatments',
  'Nails', 'Makeup', 'Facial', 'Massage', 'Waxing', 'Braids',
  'Natural Hair', 'Balayage', 'Men\'s Cuts', 'Children\'s Cuts',
  'Bridal', 'Blow Dry', 'Highlights', 'Ombre'
];

// Menu item interface
interface MenuItem {
  icon: React.ReactNode;
  title: string;
  path?: string;
  show?: boolean;
  onClick?: () => void;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);
  
  // Get user from Redux store
  const { user } = useAppSelector(state => state.auth);
  
  // Component state
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [coverPhotoLoading, setCoverPhotoLoading] = useState(false);

  // Pause idle-logout while a profile or cover photo upload is in progress.
  usePauseIdleTimer('stylist-profile-photo', photoLoading || coverPhotoLoading);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: '',
    specialties: [] as string[],
    isAvailable: true
  });
  const [stylistProfile, setStylistProfile] = useState<Partial<Stylist> | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  
  // Fetch stylist data on component mount
  useEffect(() => {
    const fetchStylistData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch stylist profile
        try {
          const profileData = await stylistService.getStylistProfile();
          setStylistProfile(profileData);
          
          // Update form data with profile info
          setFormData(prev => ({
            ...prev,
            bio: profileData.bio || '',
            specialties: profileData.specialties || [],
            isAvailable: profileData.isAvailable || true
          }));
        } catch (error) {
          console.error('Error fetching stylist profile:', error);
          // Profile fetch failed, but continue with other data
        }
        
        // Fetch portfolio (optional)
        if (user?.id) {
          try {
            const portfolioData = await portfolioService.getPortfolio(user.id);
            const mappedPortfolio: PortfolioItem[] = (portfolioData.images || []).map(img => ({
              id: img._id,
              image: img.url,
              description: img.caption || '',
              tags: img.tags || [],
              createdAt: img.createdAt || ''
            }));
            setPortfolio(mappedPortfolio);
          } catch (error) {
            console.error('Error fetching portfolio:', error);
            // Portfolio is optional, continue without it
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stylist data:', error);
        // Only show error if critical data (profile) failed
        setLoading(false);
      }
    };
    
    fetchStylistData();
  }, [user?.id]);
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData({
      ...formData,
      [name]: name === 'isAvailable' ? checked : value
    });
  };
  
  // Handle specialties change
  const handleSpecialtiesChange = (selectedSpecialties: string[]) => {
    setFormData({
      ...formData,
      specialties: selectedSpecialties
    });
  };
  
  // Toggle specialty selection
  const toggleSpecialty = (specialty: string) => {
    if (formData.specialties.includes(specialty)) {
      handleSpecialtiesChange(formData.specialties.filter(s => s !== specialty));
    } else {
      handleSpecialtiesChange([...formData.specialties, specialty]);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Update basic profile info
      await userService.updateProfile({
        name: formData.name,
        phone: formData.phone
      });
      
      // Update stylist-specific info
      await stylistService.updateStylistProfile({
        bio: formData.bio,
        specialties: formData.specialties,
        isAvailable: formData.isAvailable
      });
      
      setEditMode(false);
      setSuccess('Profile updated successfully');
      setLoading(false);
      
      // In a real app, you would dispatch actions to update the user in Redux store
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
      setLoading(false);
    }
  };
  
  // Handle profile photo upload
  const handleProfilePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    try {
      setPhotoLoading(true);
      setError(null);
      
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('photo', file);
      
      await userService.updateProfilePhoto(formData);
      
      setSuccess('Profile photo updated successfully');
      setPhotoLoading(false);
      
      // In a real app, you would dispatch an action to update the user photo in Redux store
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Failed to upload profile photo');
      setPhotoLoading(false);
    }
  };
  
  // Handle cover photo upload
  const handleCoverPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    try {
      setCoverPhotoLoading(true);
      setError(null);
      
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('photo', file);
      
      await stylistService.updateCoverPhoto(formData);
      
      // Refresh stylist profile
      const profileData = await stylistService.getStylistProfile();
      setStylistProfile(profileData);
      
      setSuccess('Cover photo updated successfully');
      setCoverPhotoLoading(false);
    } catch (error) {
      console.error('Error uploading cover photo:', error);
      setError('Failed to upload cover photo');
      setCoverPhotoLoading(false);
    }
  };
  
  // Handle profile photo deletion
  const handleDeleteProfilePhoto = async () => {
    try {
      setPhotoLoading(true);
      setError(null);
      
      await userService.deleteProfilePhoto();
      
      setSuccess('Profile photo removed successfully');
      setPhotoLoading(false);
      
      // In a real app, you would dispatch an action to remove the user photo in Redux store
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Failed to remove profile photo');
      setPhotoLoading(false);
    }
  };
  
  // Handle cover photo deletion
  const handleDeleteCoverPhoto = async () => {
    try {
      setCoverPhotoLoading(true);
      setError(null);
      
      await stylistService.deleteCoverPhoto();
      
      // Refresh stylist profile
      const profileData = await stylistService.getStylistProfile();
      setStylistProfile(profileData);
      
      setSuccess('Cover photo removed successfully');
      setCoverPhotoLoading(false);
    } catch (error) {
      console.error('Error deleting cover photo:', error);
      setError('Failed to remove cover photo');
      setCoverPhotoLoading(false);
    }
  };
  
  // Handle portfolio item deletion (unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeletePortfolioItem = async (itemId: string) => {
    try {
      await portfolioService.deletePortfolioImage(itemId);
      setPortfolio(prev => prev.filter(item => item.id !== itemId));
      setSuccess('Portfolio item removed');
    } catch (err) {
      console.error('Error deleting portfolio item:', err);
      setError('Failed to remove portfolio item');
    }
  };
  
  
  // Handle portfolio item reordering (unused but kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReorderPortfolio = async (reorderedIds: string[]) => {
    try {
      // The portfolioService doesn't have reorderPortfolio, so we'll
      // just reorder the local state for now
      // TODO: Implement the reorderPortfolio API call
      console.log('Would reorder portfolio with IDs:', reorderedIds);
      
      // Reorder the portfolio state
      const reorderedPortfolio = reorderedIds.map(id => 
        portfolio.find(item => item.id === id)
      ).filter(item => item !== undefined) as PortfolioItem[];
      
      setPortfolio(reorderedPortfolio);
      setSuccess('Portfolio reordered successfully');
    } catch (error) {
      console.error('Error reordering portfolio:', error);
      setError('Failed to reorder portfolio items');
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      setLoading(true);
      // Call logout service/action
      // await dispatch(logout());
      setLoading(false);
      // Redirect to login
      // navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to logout');
      setLoading(false);
    }
  };
  
  // Handle toast notification close
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  // Menu items based on mobile profile screen
  // Account Settings
  const accountSettings: MenuItem[] = [
    {
      icon: <EditIcon />,
      title: 'Edit Profile',
      path: '/stylist/edit-profile',
    },
    {
      icon: <NotificationsIcon />,
      title: 'Notifications',
      path: '/stylist/notifications',
    },
    {
      icon: <SecurityIcon />,
      title: 'Password & Security',
      path: '/stylist/security',
    },
    {
      icon: <span>💰</span>,
      title: 'Booking Guarantee Settings',
      path: '/stylist/down-payment-policy',
    },
  ];

  // Help & Support
  const helpSettings: MenuItem[] = [
    {
      icon: <SupportIcon />,
      title: 'Contact Support',
      path: '/stylist/support',
    },
    {
      icon: <LogoutIcon />,
      title: 'Logout',
      onClick: () => setShowLogoutConfirm(true),
    },
  ];

  // Render menu item
  const renderMenuItem = (item: MenuItem) => {
    if (item.show === false) return null;
    
    return (
      <div 
        key={item.title}
        className="menu-item"
        onClick={() => {
          if (item.path) {
            navigate(item.path);
          } else if (item.onClick) {
            item.onClick();
          }
        }}
      >
        <div className="menu-item__icon">{item.icon}</div>
        <div className="menu-item__title">{item.title}</div>
        <div className="menu-item__arrow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    );
  };
  
  return (
    <div className="stylist-profile">
      {/* Notifications */}
      {error && (
        <div className="tesla-alert tesla-alert--error">
          <span>{error}</span>
          <button className="tesla-alert__close" onClick={() => setError(null)}>
            <CloseIcon />
          </button>
        </div>
      )}
      
      {success && (
        <div className="tesla-toast">
          <span>{success}</span>
          <button className="tesla-toast__close" onClick={handleCloseSuccess}>
            <CloseIcon />
          </button>
        </div>
      )}
      
      {/* Cover Photo Section */}
      <div 
        className="cover-photo"
        style={{
          backgroundImage: stylistProfile?.coverPhoto ? 
            `url(${stylistProfile.coverPhoto})` : 
            'linear-gradient(to right, #C9A84C, #171a20)'
        }}
      >
        {coverPhotoLoading ? (
          <div className="tesla-loader"></div>
        ) : (
          <div className="cover-photo__actions">
            <button
              className="tesla-button tesla-button--secondary tesla-button--sm"
              onClick={() => coverPhotoInputRef.current?.click()}
            >
              <CameraIcon />
              {stylistProfile?.coverPhoto ? 'Change Cover' : 'Add Cover Photo'}
            </button>
            
            {stylistProfile?.coverPhoto && (
              <button
                className="tesla-button tesla-button--outline tesla-button--sm"
                onClick={handleDeleteCoverPhoto}
              >
                <DeleteIcon />
                Remove
              </button>
            )}
            
            <input
              ref={coverPhotoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleCoverPhotoUpload}
            />
          </div>
        )}
      </div>
      
      <div className="profile-layout">
        {/* Left Sidebar - Menu Options */}
        <div className="profile-sidebar">
          {/* Profile Photo */}
          <div className="profile-photo-container">
            {user?.profilePhoto?.url ? (
              <div className="profile-photo">
                <img 
                  src={user.profilePhoto.url} 
                  alt={user.name} 
                />
                {photoLoading && <div className="tesla-loader"></div>}
              </div>
            ) : (
              <div className="profile-photo-placeholder">
                {user?.name.charAt(0).toUpperCase()}
                {photoLoading && <div className="tesla-loader"></div>}
              </div>
            )}
            <button 
              className="profile-photo-edit"
              onClick={() => profilePhotoInputRef.current?.click()}
              disabled={photoLoading}
            >
              <CameraIcon />
            </button>
            
            <input
              ref={profilePhotoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfilePhotoUpload}
            />
            
            {user?.profilePhoto && (
              <button
                className="tesla-button tesla-button--outline tesla-button--sm mt-4"
                onClick={handleDeleteProfilePhoto}
                disabled={photoLoading}
              >
                <DeleteIcon />
                Remove Photo
              </button>
            )}
          </div>
          
          <div className="profile-name">{user?.name}</div>
          <div className="profile-type">Stylist</div>
          
          {/* Menu Sections */}
          <div className="menu-section">
            <div className="menu-section__title">Account Settings</div>
            {accountSettings.map(renderMenuItem)}
          </div>
          
          <div className="menu-section">
            <div className="menu-section__title">Help & Support</div>
            {helpSettings.map(renderMenuItem)}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="profile-content">
          <div className="profile-content__header">
            <h1 className="profile-content__title">My Stylist Profile</h1>
            <div className="profile-status">
              <div className={`availability-badge ${formData.isAvailable ? 'availability-badge--available' : 'availability-badge--unavailable'}`}>
                {formData.isAvailable ? "Available for Bookings" : "Unavailable"}
              </div>
              
              <div className="availability-toggle">
                <label className="tesla-switch">
                  <input
                    type="checkbox"
                    name="isAvailable"
                    checked={formData.isAvailable}
                    onChange={(e) => handleChange(e)}
                    disabled={!editMode || loading}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>
          
          {/* Profile Information Card */}
          <div className="tesla-card">
            <div className="tesla-card__header">
              <h2 className="tesla-card__title">Profile Information</h2>
              <button
                className={`tesla-button ${editMode ? "tesla-button--primary" : "tesla-button--outline"}`}
                onClick={() => {
                  if (editMode) {
                    const formEl = document.querySelector('form') as HTMLFormElement;
                    formEl?.dispatchEvent(new Event('submit', { cancelable: true }));
                  } else {
                    setEditMode(true);
                  }
                }}
                disabled={loading}
              >
                {editMode ? <SaveIcon /> : <EditIcon />}
                {editMode ? 'Save Changes' : 'Edit Profile'}
              </button>
            </div>
            
            <div className="tesla-card__content">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="tesla-input"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-icon">
                    <EmailIcon />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="tesla-input"
                      value={formData.email}
                      disabled={true} // Email can only be changed in settings
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="input-with-icon">
                    <PhoneIcon />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="tesla-input"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!editMode || loading}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    name="bio"
                    className="tesla-textarea"
                    value={formData.bio}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    rows={4}
                    placeholder="Tell clients about yourself, your experience, and your style"
                  />
                </div>
                
                <div className="form-group">
                  <label>Specialties</label>
                  <div className="specialty-tags">
                    {SPECIALTIES_OPTIONS.map(specialty => (
                      <div 
                        key={specialty}
                        className={`specialty-tag ${formData.specialties.includes(specialty) ? 'active' : ''}`}
                        onClick={() => editMode && !loading ? toggleSpecialty(specialty) : null}
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                </div>
                
                {editMode && (
                  <div className="form-actions">
                    <button
                      type="button"
                      className="tesla-button tesla-button--outline"
                      onClick={() => setEditMode(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    
                    <button
                      type="submit"
                      className="tesla-button tesla-button--primary"
                      disabled={loading}
                    >
                      {loading ? <div className="tesla-loader tesla-loader--sm"></div> : <SaveIcon />}
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
          
          {/* Portfolio Card */}
          <div className="tesla-card">
            <div className="tesla-card__header">
              <h2 className="tesla-card__title">
                <PortfolioIcon /> Portfolio
              </h2>
              
              <button
                className="tesla-button tesla-button--primary"
                onClick={() => navigate('/stylist/portfolio')}
              >
                Manage Portfolio
              </button>
            </div>
            
            <div className="tesla-card__content">
              {portfolio.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">
                    <PortfolioIcon size={48} />
                  </div>
                  <h3 className="empty-state__title">No Portfolio Items Yet</h3>
                  <p className="empty-state__text">
                    Showcase your work by adding photos to your portfolio.
                  </p>
                  <button
                    className="tesla-button tesla-button--primary"
                    onClick={() => navigate('/stylist/portfolio/add')}
                  >
                    <AddPhotoIcon />
                    Add Portfolio Items
                  </button>
                </div>
              ) : (
                <div className="portfolio-gallery">
                  {portfolio.slice(0, 6).map((item) => (
                    <div className="portfolio-item" key={item.id}>
                      <img src={item.image} alt={item.description} />
                    </div>
                  ))}
                  
                  {portfolio.length > 6 && (
                    <button
                      className="tesla-button tesla-button--outline tesla-button--sm view-all-btn"
                      onClick={() => navigate('/stylist/portfolio')}
                    >
                      View All ({portfolio.length})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="tesla-modal">
          <div className="tesla-modal__backdrop" onClick={() => setShowLogoutConfirm(false)}></div>
          <div className="tesla-modal__container">
            <div className="tesla-modal__header">
              <h2 className="tesla-modal__title">Confirm Logout</h2>
              <button 
                className="tesla-modal__close"
                onClick={() => setShowLogoutConfirm(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="tesla-modal__content">
              <p>Are you sure you want to logout?</p>
            </div>
            <div className="tesla-modal__actions">
              <button 
                className="tesla-button tesla-button--outline"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="tesla-button tesla-button--primary"
                onClick={handleLogout}
              >
                {loading ? <div className="tesla-loader tesla-loader--sm"></div> : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;